import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import RichTextEditor from "@/components/RichTextEditor";
import AIMetadataPanel from "@/components/AIMetadataPanel";
import SharePreviewCard from "@/components/SharePreviewCard";
import ImageGeneratorCard from "@/components/ImageGeneratorCard";
import { ArrowLeft, Save, Send, Clock, ExternalLink, Trash2, Bot } from "lucide-react";

const CATEGORIES = [
  "collective-agreements",
  "contract-interpretation",
  "labour-law-basics",
  "hr-compliance",
  "arbitration-grievances",
  "guides-explainers",
  "case-examples",
];

const CATEGORY_LABELS = {
  "collective-agreements": "Collective Agreements",
  "contract-interpretation": "Contract Interpretation",
  "labour-law-basics": "Labour Law Basics",
  "hr-compliance": "HR & Compliance",
  "arbitration-grievances": "Arbitration & Grievances",
  "guides-explainers": "Guides & Explainers",
  "case-examples": "Case Examples",
};

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [status, setStatus] = useState("draft");
  const [excerpt, setExcerpt] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [categories, setCategories] = useState([]);
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");

  // UI state
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // AI metadata state
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [aiMetadata, setAIMetadata] = useState(null);

  // Generate slug from title
  const generateSlug = useCallback((text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  }, []);

  // Auto-generate slug from title if not manually edited
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setSlug(generateSlug(title));
    }
  }, [title, slugManuallyEdited, generateSlug]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedChanges(true);
    }
  }, [title, slug, status, excerpt, contentHtml, categories, featuredImageUrl, seoTitle, seoDescription, scheduledFor]);

  // Fetch existing post for editing
  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/blog/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const post = response.data;
      
      setTitle(post.title || "");
      setSlug(post.slug || "");
      setSlugManuallyEdited(true); // Don't auto-generate for existing posts
      setStatus(post.status || "draft");
      setExcerpt(post.excerpt || "");
      setContentHtml(post.content_html || "");
      setCategories(post.categories || []);
      setFeaturedImageUrl(post.featured_image_url || "");
      setSeoTitle(post.seo_title || "");
      setSeoDescription(post.seo_description || "");
      
      // Check for AI metadata
      setIsAIGenerated(post.ai_generated || false);
      setAIMetadata(post.ai_metadata || null);
      
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error("Failed to load post");
      console.error(error);
      navigate("/admin/blog");
    } finally {
      setLoading(false);
    }
  };

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = async (saveStatus = status) => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!slug.trim()) {
      toast.error("Slug is required");
      return;
    }
    if (!contentHtml.trim()) {
      toast.error("Content is required");
      return;
    }

    // Validate scheduled date if scheduling
    if (saveStatus === "scheduled" && !scheduledFor) {
      toast.error("Please select a publish date for scheduling");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setSaving(true);

    try {
      const postData = {
        title: title.trim(),
        slug: slug.trim(),
        status: saveStatus,
        excerpt: excerpt.trim() || null,
        content_html: contentHtml,
        categories,
        featured_image_url: featuredImageUrl.trim() || null,
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
      };

      if (saveStatus === "scheduled" && scheduledFor) {
        postData.scheduled_for = new Date(scheduledFor).toISOString();
      }

      if (isEditing) {
        await axios.put(`${API}/admin/blog/posts/${id}`, postData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Post updated");
      } else {
        const response = await axios.post(`${API}/admin/blog/posts`, postData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Post created");
        // Navigate to edit page for the new post
        navigate(`/admin/blog/${response.data.id}`, { replace: true });
      }
      
      setHasUnsavedChanges(false);
      setStatus(saveStatus);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save post";
      toast.error(message);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isEditing) {
      // Use the publish endpoint
      const token = localStorage.getItem("admin_token");
      setSaving(true);
      try {
        // First save any pending changes
        await handleSave("draft");
        // Then publish
        await axios.post(`${API}/admin/blog/posts/${id}/publish`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Post published");
        setStatus("published");
        setHasUnsavedChanges(false);
      } catch (error) {
        toast.error("Failed to publish post");
        console.error(error);
      } finally {
        setSaving(false);
      }
    } else {
      await handleSave("published");
    }
  };

  const handleSchedule = async () => {
    if (!scheduledFor) {
      toast.error("Please select a publish date");
      return;
    }
    await handleSave("scheduled");
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("admin_token");
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/blog/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Post deleted");
      setHasUnsavedChanges(false);
      navigate("/admin/blog");
    } catch (error) {
      toast.error("Failed to delete post");
      console.error(error);
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  const toggleCategory = (cat) => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handlePreview = () => {
    if (slug) {
      window.open(`/blog/${slug}`, "_blank");
    } else {
      toast.error("Save the post first to preview");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-blog-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/blog")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold">
              {isEditing ? "Edit Post" : "New Post"}
            </h1>
            {isEditing && (
              <p className="text-sm text-muted-foreground">
                Last saved: {hasUnsavedChanges ? "Unsaved changes" : "Up to date"}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {status === "published" && (
            <Button
              variant="outline"
              onClick={handlePreview}
              data-testid="preview-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSave("draft")}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button
            onClick={handlePublish}
            disabled={saving}
            data-testid="publish-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {status === "published" ? "Update" : "Publish"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter post title"
              className="text-lg"
              data-testid="title-input"
            />
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label htmlFor="slug">Slug *</Label>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">/blog/</span>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => {
                  setSlug(generateSlug(e.target.value));
                  setSlugManuallyEdited(true);
                }}
                placeholder="post-url-slug"
                data-testid="slug-input"
              />
            </div>
          </div>

          {/* Excerpt */}
          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Brief summary of the post (shown in listings)"
              rows={2}
              data-testid="excerpt-input"
            />
          </div>

          {/* Content Editor */}
          <div className="space-y-2">
            <Label>Content *</Label>
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              placeholder="Write your blog post content..."
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-medium">Status</h3>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>

            {status === "scheduled" && (
              <div className="space-y-2">
                <Label htmlFor="scheduledFor">Publish Date</Label>
                <Input
                  id="scheduledFor"
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  data-testid="scheduled-date-input"
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleSchedule}
                  disabled={saving || !scheduledFor}
                  data-testid="schedule-btn"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule
                </Button>
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-medium">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <Badge
                  key={cat}
                  variant={categories.includes(cat) ? "default" : "outline"}
                  className="cursor-pointer transition-colors"
                  onClick={() => toggleCategory(cat)}
                  data-testid={`category-${cat}`}
                >
                  {CATEGORY_LABELS[cat] || cat}
                </Badge>
              ))}
            </div>
            {categories.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Selected: {categories.length}
              </div>
            )}
          </div>

          {/* Featured Image */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-medium">Featured Image</h3>
            <Input
              value={featuredImageUrl}
              onChange={(e) => setFeaturedImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              data-testid="featured-image-input"
            />
            {featuredImageUrl && (
              <div className="relative aspect-video bg-muted rounded overflow-hidden">
                <img
                  src={featuredImageUrl}
                  alt="Featured"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-medium">SEO Settings</h3>
            <div className="space-y-2">
              <Label htmlFor="seoTitle">SEO Title</Label>
              <Input
                id="seoTitle"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                placeholder="Custom title for search engines"
                maxLength={70}
                data-testid="seo-title-input"
              />
              <p className="text-xs text-muted-foreground">{seoTitle.length}/70</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seoDescription">Meta Description</Label>
              <Textarea
                id="seoDescription"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                placeholder="Description for search results"
                rows={3}
                maxLength={160}
                data-testid="seo-description-input"
              />
              <p className="text-xs text-muted-foreground">{seoDescription.length}/160</p>
            </div>
          </div>

          {/* AI Metadata Panel */}
          {isAIGenerated && aiMetadata && (
            <AIMetadataPanel 
              metadata={aiMetadata} 
              postId={id}
              onFeedbackSubmit={() => toast.success("Feedback submitted")}
            />
          )}

          {/* Image Generator Card */}
          {isEditing && (
            <ImageGeneratorCard
              postId={id}
              currentImageUrl={featuredImageUrl}
              postTitle={title}
              onImageGenerated={(newImageUrl) => {
                setFeaturedImageUrl(newImageUrl);
                setHasUnsavedChanges(true);
              }}
            />
          )}

          {/* Share Preview Card */}
          {isEditing && slug && (
            <SharePreviewCard 
              post={{
                slug,
                title,
                excerpt,
                seo_title: seoTitle,
                seo_description: seoDescription,
                featured_image_url: featuredImageUrl,
                og_image_url: featuredImageUrl,
                status
              }}
            />
          )}

          {/* Delete */}
          {isEditing && (
            <div className="border border-destructive/20 rounded-lg p-4">
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="delete-post-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Post
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
